/**
 * Public Customer Upload — branded landing where the customer drops their
 * ID / passport / age proof. NO AUTH. Mounted at:
 *   /connection/upload/:slug
 *
 * The operator generates this URL from the Query Workspace and shares it
 * with the customer over WhatsApp. The customer opens it on their phone,
 * picks/captures files, and they land directly as attachments on the
 * parent query.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Upload } from 'lucide-react';
import { API } from '@/App';

const ACCEPT = 'application/pdf,image/png,image/jpeg';
const MAX_BYTES = 15 * 1024 * 1024;

const PublicCustomerUpload = () => {
  const { slug } = useParams();
  const [meta, setMeta] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | missing
  const [uploaderName, setUploaderName] = useState('');
  const [docLabel, setDocLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [received, setReceived] = useState([]);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetch(`${API}/public/upload/${slug}`);
        if (!res.ok) { if (live) setStatus('missing'); return; }
        const data = await res.json();
        if (live) {
          setMeta(data);
          setUploaderName(data.customer_name && data.customer_name !== 'there' ? data.customer_name : '');
          setStatus('ok');
        }
      } catch (e) {
        if (live) setStatus('missing');
      }
    })();
    return () => { live = false; };
  }, [slug]);

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    if (file.size > MAX_BYTES) { setError('File too large (max 15 MB)'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('uploader_name', uploaderName.trim());
      fd.append('doc_label', docLabel.trim());
      const res = await fetch(`${API}/public/upload/${slug}`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      setReceived(r => [{ name: data.filename, label: docLabel.trim(), at: new Date() }, ...r]);
      setDocLabel('');
    } catch (e) { setError(e.message); }
    finally { setUploading(false); }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fdf6e6] text-[#3f2e3e]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (status === 'missing') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#fdf6e6] text-[#3f2e3e] p-6 text-center">
        <div className="text-xl font-semibold mb-2">This upload link is no longer available.</div>
        <p className="text-sm text-[#6b5566] max-w-xs">
          If you were expecting it, please get in touch with your Pollen Dots travel curator.
        </p>
        <a href="https://www.pollendots.com" className="mt-6 text-[#b16e32] font-semibold border-b border-[#b16e3266] pb-0.5">
          Visit Pollen Dots →
        </a>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#fdf6e6] text-[#3f2e3e]" data-testid="public-upload-page">
      {/* Branded top */}
      <div className="sticky top-0 z-10 flex items-center px-4 py-2.5 bg-[#fdf6e6] border-b border-[#e9d8b9]">
        <div className="flex flex-col items-start leading-tight">
          <img src="/og/pollen-logo.png" alt="Pollen Dots" className="h-7 sm:h-8 w-auto" />
          <div className="text-[10px] sm:text-[11px] text-[#7a5a48] mt-0.5 tracking-wide ml-[34px] sm:ml-[40px]">Travel · Connect · Care</div>
        </div>
      </div>

      <div className="flex-1 px-5 py-6 max-w-md w-full mx-auto space-y-5">
        <div>
          <h1 className="text-xl font-semibold">Hi {meta.customer_name === 'there' ? 'there' : meta.customer_name},</h1>
          <p className="text-sm text-[#6b5566] mt-1.5">
            Please upload your documents (ID / passport / age proof). PDF, JPG or PNG up to 15&nbsp;MB each.
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-wide text-[#7a5a48] mb-1.5">Your name</label>
            <input
              value={uploaderName}
              onChange={e => setUploaderName(e.target.value)}
              placeholder="Full name"
              className="w-full h-11 px-3 rounded-lg border border-[#e9d8b9] bg-white text-sm outline-none focus:border-[#b16e32]"
              data-testid="upload-name-input"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide text-[#7a5a48] mb-1.5">What is this document?</label>
            <input
              value={docLabel}
              onChange={e => setDocLabel(e.target.value)}
              placeholder='e.g. "Aadhaar front", "Passport bio page"'
              className="w-full h-11 px-3 rounded-lg border border-[#e9d8b9] bg-white text-sm outline-none focus:border-[#b16e32]"
              data-testid="upload-label-input"
            />
          </div>

          <label className="block">
            <span className="sr-only">Choose file</span>
            <span className={`flex items-center justify-center gap-2 h-12 rounded-lg text-sm font-semibold cursor-pointer ${uploading ? 'bg-[#e9d8b9] text-[#7a5a48]' : 'bg-[#b16e32] text-white'}`} data-testid="upload-cta">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? 'Uploading…' : 'Choose file & upload'}
            </span>
            <input
              type="file"
              accept={ACCEPT}
              className="hidden"
              disabled={uploading}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
              data-testid="upload-input"
            />
          </label>
          {error && <div className="text-sm text-[#a14545]">{error}</div>}
        </div>

        {received.length > 0 && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 space-y-2" data-testid="upload-received">
            <div className="text-[11px] uppercase tracking-wide text-emerald-800 font-semibold">Received</div>
            {received.map((r, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
                <div className="min-w-0">
                  <div className="truncate font-medium">{r.name}</div>
                  {r.label && <div className="text-xs text-emerald-900/70">{r.label}</div>}
                </div>
              </div>
            ))}
            <p className="text-[11px] text-emerald-900/70 pt-1">
              You can keep uploading. Your curator is notified automatically.
            </p>
          </div>
        )}
      </div>

      <div className="text-center text-[10px] text-[#7a5a48] py-3 border-t border-[#e9d8b9]/60">
        Crafted with care by <span className="font-semibold text-[#b16e32]">Pollen Dots</span> · Travel · Connect · Care
      </div>
    </div>
  );
};

export default PublicCustomerUpload;
